import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  AdvancedSearchURLParams,
  PersonSearchParamsv2,
  RelatedReport,
} from '@ltvco/refresh-lib/v2';
import { useContext, useState } from 'react';

import {
  CHATBV_GA4_EVENTS,
  NULL_SEARCH_PARAMS,
  SEARCH_TYPES,
} from 'components/ChatBV/constants';
import {
  GenericReportResponse,
  Message,
  MessageReportData,
} from 'components/ChatBV/types';
import {
  getPersonPermalink,
  getRelatedReportPermalink,
  TransformedReportData,
  getRelatedReportData,
} from 'components/ChatBV/utils';

export const useReportState = () => {
  const [openReport, setOpenReport] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [openTeaser, setOpenTeaser] = useState(false);
  const [permalink, setPermalink] = useState<string | undefined>(undefined);
  const [reportData, setReportData] = useState<
    GenericReportResponse | undefined
  >(undefined);
  const [type, setType] = useState<string | undefined>(undefined);
  const { trackEventGA4, logError } = useContext(AppConfig);
  const [bvid, setBvid] = useState<string | undefined>(undefined);
  const [searchParams, setSearchParams] =
    useState<AdvancedSearchURLParams>(NULL_SEARCH_PARAMS);

  const closeAllpanels = () => {
    setOpenReport(false);
    setOpenFilters(false);
    setOpenTeaser(false);
    setPermalink(undefined);
    setReportData(undefined);
    setType(undefined);
    setBvid(undefined);
    setSearchParams(NULL_SEARCH_PARAMS);
  };

  const handleButtonClick =
    //TODO: check if we need this arg in the function
    // eslint-disable-next-line no-unused-vars
    (_message: Message) => (report: MessageReportData) => {
      if (report) {
        setPermalink(report.permalink);
        setReportData(report.reportData);
        setType(report.type);
        setOpenReport(true);
        setOpenFilters(false);
        setOpenTeaser(false);
      }
    };

  const handleCloseReport = () => {
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.report.type,
      subtype: CHATBV_GA4_EVENTS.report.subTypes.close_report,
    });
    closeAllpanels();
  };

  const handleChatTeaserClick =
    (message: Message | null, typeOverride?: string) =>
    async (bvid: string) => {
      if (bvid) {
        setOpenReport(true);
        setOpenFilters(false);
        setOpenTeaser(false);
        setBvid(bvid);
        try {
          const { error, permalink } = await getPersonPermalink(bvid);
          if (error) {
            throw new Error(`Error getting person permalink: ${error}`);
          }
          if (permalink) {
            setType(typeOverride || message?.teaser?.[0].type || '');
            setPermalink(permalink);
          } else {
            throw new Error('No permalink found');
          }
        } catch (error) {
          closeAllpanels();
          logError('Error getting person permalink:', error as Error);
        }
      }
    };

  const handleOpenTeaser = () => {
    setOpenTeaser(true);
    setOpenReport(false);
    setOpenFilters(false);
  };

  const handleCloseTeaser = () => {
    closeAllpanels();
  };

  const setParamsFromMessage = (message: Message) => {
    const newParams = {
      ...NULL_SEARCH_PARAMS,
      fName: message.params?.first_name || '',
      lName: message.params?.last_name || '',
      fullName:
        message.params?.first_name && message.params?.last_name
          ? `${message.params?.first_name} ${message.params?.last_name}`
          : '',
      birthMonth: message.params?.birth_month || '',
      birthYear: message.params?.birth_year || '',
      deathMonth: message.params?.death_month || '',
      deathYear: message.params?.death_year || '',
      city: message.params?.city || '',
      state: message.params?.state || '',
      age: message.params?.age || '',
      company: message.params?.company || '',
      industry: message.params?.industry || '',
      title: message.params?.title || '',
      middle_name: message.params?.middle_name || '',
    };
    setSearchParams({ ...searchParams, ...newParams });
  };

  const setParamsFromPersonSearchParams = (params: PersonSearchParamsv2) => {
    const newParams = {
      ...NULL_SEARCH_PARAMS,
      ...params,
    };
    setSearchParams({ ...searchParams, ...newParams });
  };

  const handleViewAllResultsClick = (message: Message) => () => {
    setParamsFromMessage(message);
    setOpenReport(false);
    setOpenFilters(false);
    setOpenTeaser(true);
  };

  const handleFiltersClick = (message: Message) => () => {
    setParamsFromMessage(message);
    setOpenFilters(true);
    setOpenReport(false);
    setOpenTeaser(false);
  };

  const handleOpenFilters = () => {
    setOpenFilters(true);
    setOpenReport(false);
    setOpenTeaser(false);
  };

  const handleCloseFilters = () => {
    closeAllpanels();
  };

  const handleViewAllResultsClickFromPersonSearchParams = (
    params: PersonSearchParamsv2
  ) => {
    setParamsFromPersonSearchParams(params);
    setOpenReport(false);
    setOpenFilters(false);
    setOpenTeaser(true);
  };

  const handleButtonClickFromFollowUpMessage = (message: Message) => () => {
    setPermalink(message.permalink);
    setType('person');
    setOpenReport(true);
    setOpenFilters(false);
    setOpenTeaser(false);
  };

  const handleObituaryClick = (permalink: string) => {
    setPermalink(permalink);
    setType('obituary');
    setOpenReport(true);
    setOpenFilters(false);
    setOpenTeaser(false);
  };

  const handleRelatedReportClick = async (relatedReport: RelatedReport) => {
    setOpenReport(false);
    setOpenFilters(false);
    setOpenTeaser(false);

    try {
      const transformedData = getRelatedReportData(relatedReport);

      if (transformedData.reportType === 'teaser') {
        if (transformedData.personSearch) {
          setParamsFromPersonSearchParams(transformedData.personSearch);
          setOpenTeaser(true);
        }
        return;
      }

      if (transformedData.useBvid && transformedData.data?.bvid) {
        setOpenReport(true);
        setBvid(transformedData.data.bvid);

        const { error, permalink } = await getPersonPermalink(
          transformedData.data.bvid
        );
        if (error) {
          throw new Error(`Error getting person permalink: ${error}`);
        }
        if (permalink) {
          setType(SEARCH_TYPES.PERSON);
          setPermalink(permalink);
        } else {
          throw new Error('No permalink found');
        }
        return;
      }

      if (
        transformedData.reportType === SEARCH_TYPES.OBITUARY &&
        transformedData.data?.permalink
      ) {
        // Handle obituary with direct permalink
        setPermalink(transformedData.data.permalink as string);
        setType(SEARCH_TYPES.OBITUARY);
        setOpenReport(true);
        return;
      }

      // Handle other report types (phone, email, property, vehicle)
      if (transformedData.data) {
        setOpenReport(true);

        const permalinkResult = await getRelatedReportPermalink(
          transformedData.reportType,
          transformedData.data as TransformedReportData
        );

        if (permalinkResult?.permalink) {
          setType(transformedData.reportType);
          setPermalink(permalinkResult.permalink);
          setReportData(undefined);
        } else {
          throw new Error(`No ${transformedData.reportType} permalink found`);
        }
      }
    } catch (error) {
      closeAllpanels();
      logError(`Error handling related report click:`, error as Error);
    }
  };

  return {
    openReport,
    setOpenReport,
    openFilters,
    setOpenFilters,
    openTeaser,
    setOpenTeaser,
    searchParams,
    setSearchParams,
    permalink,
    reportData,
    type,
    bvid,
    handleButtonClick,
    handleCloseReport,
    handleChatTeaserClick,
    handleViewAllResultsClick,
    handleViewAllResultsClickFromPersonSearchParams,
    handleButtonClickFromFollowUpMessage,
    handleFiltersClick,
    handleOpenFilters,
    handleCloseFilters,
    handleOpenTeaser,
    handleCloseTeaser,
    handleObituaryClick,
    handleRelatedReportClick,
    closeAllpanels,
  };
};
